import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useGtt } from "../lib/gtt";
import { fmt, isDerivative } from "../lib/format";

const inputCls = "w-full h-9 px-2.5 rounded-md border border-input bg-background num text-[13px] outline-none focus:border-primary";
const pill = (on, tone = "") => `h-7 px-3 rounded-full text-[11px] font-medium border transition-colors duration-150 ${on ? tone || "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"}`;
const r2 = (x, t) => Math.round(x / t) * t;

function initial(edit, side, ltp, tick, lot, der, pre) {
  if (edit) {
    const legs = edit.legs.map((l) => ({ t: l.trigger_price, p: l.price }));
    return { type: edit.type, side: edit.side, product: edit.product, lots: edit.quantity / lot, legs: edit.type === "oco" && edit.side === "BUY" ? legs.reverse() : legs };
  }
  const s = side || "SELL";
  const near = (m) => +r2(ltp * m, tick).toFixed(2);
  const ok = pre?.product && (der ? pre.product !== "CNC" : pre.product !== "NRML");
  const [slM, tpM] = s === "SELL" ? [0.95, 1.05] : [1.05, 0.95];
  return { type: pre?.type || "single", side: s, product: ok ? pre.product : der ? "NRML" : "CNC", lots: pre?.lots || 1, single: { t: near(s === "SELL" ? 1.05 : 0.95), p: near(s === "SELL" ? 1.05 : 0.95) }, legs: null, oco: [{ t: near(slM), p: near(slM) }, { t: near(tpM), p: near(tpM) }] };
}

function LegRow({ label, hint, leg, onChange, testid, tick }) {
  return (
    <div className="grid grid-cols-[90px_1fr_1fr] gap-2 items-end">
      <div className="text-[11px] font-medium pb-2">{label}<span className="block text-[10px] text-muted-foreground font-normal">{hint}</span></div>
      <label><span className="eyebrow block mb-1">Trigger ₹</span><input data-testid={`${testid}-trigger`} type="number" step={tick} value={leg.t} onChange={(e) => onChange({ ...leg, t: e.target.value })} className={inputCls} /></label>
      <label><span className="eyebrow block mb-1">Limit price ₹</span><input data-testid={`${testid}-price`} type="number" step={tick} value={leg.p} onChange={(e) => onChange({ ...leg, p: e.target.value })} className={inputCls} /></label>
    </div>
  );
}

export function GttForm({ inst, tick, edit, side, pre }) {
  const lot = inst.lot_size || 1;
  const ts = inst.tick_size || 0.05;
  const ltp = tick?.ltp || edit?.ltp_at_create || 0;
  const [f, setF] = useState(() => {
    const i = initial(edit, side, ltp, ts, lot, isDerivative(inst), pre);
    if (edit) return { ...i, single: edit.type === "single" ? i.legs[0] : { t: "", p: "" }, oco: edit.type === "oco" ? i.legs : [{ t: "", p: "" }, { t: "", p: "" }] };
    return i;
  });
  const [busy, setBusy] = useState(false);
  const { close, bump } = useGtt();
  const set = (p) => setF((x) => {
    const n = { ...x, ...p };
    return p.side && p.side !== x.side && !edit ? { ...n, oco: [n.oco[1], n.oco[0]] } : n;
  });
  const der = isDerivative(inst);
  const pct = (v) => (ltp && v ? `${(((v - ltp) / ltp) * 100).toFixed(2)}% from LTP` : "");
  const submit = async () => {
    const legs = (f.type === "single" ? [f.single] : f.oco).map((l) => ({ trigger_price: Number(l.t), price: Number(l.p) }));
    const body = { instrument_key: inst.instrument_key, type: f.type, side: f.side, product: f.product, quantity: Math.max(1, Math.round(Number(f.lots) || 0)) * lot, legs };
    setBusy(true);
    try {
      if (edit) await api.put(`/gtt/${edit.id}`, body); else await api.post("/gtt", body);
      toast.success(`Simulated order · GTT ${edit ? "modified" : "created"} · ${inst.trading_symbol}`, { description: "A simulated limit order is placed automatically when the trigger is hit." });
      bump(); close();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid="gtt-form">
      <div className="flex flex-wrap gap-1.5">
        {[["single", "Single trigger"], ["oco", "OCO (target + stop-loss)"]].map(([v, l]) => <button key={v} data-testid={`gtt-type-${v}`} onClick={() => set({ type: v })} className={pill(f.type === v)}>{l}</button>)}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {["BUY", "SELL"].map((v) => <button key={v} data-testid={`gtt-side-${v}`} onClick={() => set({ side: v })} className={pill(f.side === v, v === "BUY" ? "border-up text-up bg-up/10" : "border-down text-down bg-down/10")}>{v}</button>)}
        <span className="w-3" />
        {(der ? ["NRML", "MIS"] : ["CNC", "MIS"]).map((v) => <button key={v} data-testid={`gtt-product-${v}`} onClick={() => set({ product: v })} className={pill(f.product === v)}>{v}</button>)}
      </div>
      <label className="block w-40"><span className="eyebrow block mb-1">{lot > 1 ? `Lots (×${lot})` : "Quantity"}</span>
        <input data-testid="gtt-quantity-input" type="number" min="1" value={f.lots} onChange={(e) => set({ lots: e.target.value })} className={inputCls} /></label>
      {f.type === "single"
        ? <LegRow label="Trigger" hint={pct(Number(f.single.t))} leg={f.single} tick={ts} testid="gtt-single" onChange={(single) => set({ single })} />
        : (<>
          <LegRow label="Stop-loss" hint={pct(Number(f.oco[0].t))} leg={f.oco[0]} tick={ts} testid="gtt-sl" onChange={(l) => set({ oco: [l, f.oco[1]] })} />
          <LegRow label="Target" hint={pct(Number(f.oco[1].t))} leg={f.oco[1]} tick={ts} testid="gtt-target" onChange={(l) => set({ oco: [f.oco[0], l] })} />
        </>)}
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        When LTP (now ₹{fmt(ltp)}) reaches the trigger during market hours, a {f.side} LIMIT order is placed at your limit price.
        {f.type === "oco" && ` Stop-loss must be ${f.side === "SELL" ? "below" : "above"} and target ${f.side === "SELL" ? "above" : "below"} the current price. Whichever hits first fires; the other is cancelled.`} Valid for 1 year.
      </p>
      <button data-testid="gtt-submit-button" disabled={busy} onClick={submit}
        className={`w-full h-10 rounded-md text-white font-semibold text-[13px] flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.99] transition-[filter,transform] duration-150 disabled:opacity-60 ${f.side === "BUY" ? "bg-up" : "bg-down"}`}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}{edit ? "Modify GTT" : "Create GTT"}
      </button>
    </div>
  );
}
